import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled, {useTheme} from 'styled-components';

import {TooltipCoordinator} from '../tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../tooltips/tooltipOverflowCondition';
import {typographyMixin} from '../typography/typography';

/*
 * Props.
 */

export interface DropdownHeaderProps {
  title?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hasTitleTooltip?: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  position: relative;
  /* Avoids it from shrinking when used in a dropdown with a maxHeight */
  flex-shrink: 0;

  display: flex;
  flex-direction: column;

  padding: ${(p) => (p.theme.hasDropdownRedesign ? 0 : '12px 7px 0px 7px')};
  border-bottom: ${(p) =>
    p.theme.hasDropdownRedesign
      ? `1px solid ${p.theme.border.tertiary}`
      : `1px solid ${p.theme.border.secondary}`};
`;

const StyledTopDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: ${(p) => (p.theme.hasDropdownRedesign ? '12px 6px' : '0 0 12px 0')};
`;

const StyledLeftDiv = styled.div`
  grid-row: 1;
  grid-column: 1;
  justify-self: start;
  margin-block: -2px;
`;

const StyledRightDiv = styled.div`
  grid-row: 1;
  grid-column: 3;
  justify-self: end;
  margin-block: -2px;
`;

const StyledTitleDiv = styled.div`
  grid-row: 1;
  grid-column: 2;
  padding: 0 6px;

  text-align: center;
  ${typographyMixin({
    variant: 'body-medium',
    color: 'primary',
    shouldTruncate: true,
  })}
`;

const StyledContentDiv = styled.div`
  padding: ${(p) => (p.theme.hasDropdownRedesign ? 0 : '0 6px')};
`;

const StyledHeaderDividerDiv = styled.div`
  height: 1px;
  border-top: 1px solid ${(p) => p.theme.border.tertiary};
`;

/*
 * Component.
 */

export const DropdownHeader: FC<PropsWithChildren<DropdownHeaderProps>> = (props) => {
  const theme = useTheme();
  const topSection = maybeRenderTop(props);
  const hasTop = topSection !== null;
  const hasContent = !!props.children;
  const shouldRenderDivider = hasTop && hasContent && theme.hasDropdownRedesign;

  return (
    <StyledHeaderDiv>
      {topSection}
      {shouldRenderDivider && <StyledHeaderDividerDiv />}
      <StyledContentDiv>{props.children}</StyledContentDiv>
    </StyledHeaderDiv>
  );
};

function maybeRenderTop(props: DropdownHeaderProps) {
  const {leftIcon, title, rightIcon} = props;
  if (!leftIcon && !title && !rightIcon) {
    return null;
  }

  return (
    <StyledTopDiv>
      <StyledLeftDiv>{props.leftIcon}</StyledLeftDiv>
      <TooltipCoordinator render={props.hasTitleTooltip ? () => props.title : false}>
        <StyledTitleDiv>
          <TooltipOverflowCondition>{props.title}</TooltipOverflowCondition>
        </StyledTitleDiv>
      </TooltipCoordinator>
      <StyledRightDiv>{props.rightIcon}</StyledRightDiv>
    </StyledTopDiv>
  );
}
